import { computed } from '@ember/object';
import { isPresent } from '@ember/utils';
import { and, readOnly, equal, or, gt, notEmpty } from '@ember/object/computed';
import { ProtoBaseModel, attr } from '@square/dashboard-data/utils/ember-model-stubs';
import SettlementDetails from '@square/dashboard-data/models/squareup/bills/card-tender/settlement-details';
import CreatorDetails from '@square/dashboard-data/models/squareup/beemo/api/v2/models/creator-details';
import { RiskEvaluationDetails } from '@square/dashboard-data/models/squareup/risk/risk-evaluation-details';
import Customer from '@square/dashboard-data/models/squareup/beemo/api/v2/models/customer';
import DateTime from '@square/dashboard-data/models/squareup/common/time/date-time';
import Enum from '@square/dashboard-utilities/utils/enum';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import PayWithSquareEntryMethod, { EXTERNAL_API_ENTRY_METHODS } from '@square/dashboard-data/models/squareup/common/pay-with-square-entry-method';
import Contact from '@square/dashboard-data/models/directory/contact';
import DisputedPayment from '@square/dashboard-data/models/squareup/cbms/disputed-payment';
import OtherTenderType from '@square/dashboard-data/models/squareup/beemo/api/v2/models/other-tender-type';
import ProcessingFeeAmounts from '@square/dashboard-data/models/squareup/beemo/api/v2/models/processing-fee-amounts';
import ExternalTender from '@square/dashboard-data/models/squareup/beemo/api/v2/models/external-tender';
import Device from '@square/dashboard-data/models/squareup/beemo/api/v2/models/device';
import PushTender from '@square/dashboard-data/models/squareup/beemo/api/v2/models/push-tender';
import SquareAccountTender from '@square/dashboard-data/models/squareup/beemo/api/v2/models/square-account-tender';
import { TenderType } from './tender-type';
import CurrencyExchange from '@square/dashboard-data/models/squareup/beemo/api/v2/models/currency-exchange';
import Money from '@square/dashboard-money/models/money';
export const Type = new Enum(['CASH', 'CREDIT_CARD', 'GENERIC', 'LEDGER', 'NO_SALE', 'OTHER', 'SQUARE_ACCOUNT', 'SQUARE_BALANCE', 'SQUARE_CAPITAL', 'SQUARE_GIFT_CARD', 'TAB', 'ZERO_AMOUNT', 'EXTERNAL', 'EMONEY', 'PUSH_TENDER', 'BANK_TRANSFER']);
export const CardBrand = new Enum(['UNKNOWN', 'VISA', 'MASTERCARD', 'MASTER_CARD', 'AMERICAN_EXPRESS', 'DISCOVER', 'DISCOVER_DINERS', 'INTERAC', 'JCB', 'UNION_PAY', 'SQUARE_GIFT_CARD_V2', 'SQUARE_CAPITAL_CARD', 'FELICA', 'AFTERPAY', 'CASH_APP', 'EFTPOS', 'ALIPAY', 'EBT', 'PAYPAY', 'SQUARE_ACCOUNT_BALANCE'
// if you add an entry here, also add to translations under common.cardBrands and leave this comment last
]);
export const FelicaBrand = new Enum(['UNKNOWN', 'FELICA_ID', 'FELICA_TRANSPORTATION', 'FELICA_QP'
// if you add an entry here, also add to translations under common.felicaBrands and leave this comment last
]);
export default ProtoBaseModel.extend({
  type: attr(Type),
  paymentToken: attr(String),
  totalMoney: attr(MoneyProto),
  paymentNote: attr(String),
  entryMethod: attr(PayWithSquareEntryMethod),
  cardBrand: attr(CardBrand),
  felicaBrand: attr(FelicaBrand),
  panSuffix: attr(String),
  processingFeeMoney: attr(MoneyProto),
  processingFeeAmounts: attr(ProcessingFeeAmounts),
  sequentialTenderNumber: attr(String),
  buyerCurrencyExchange: attr(CurrencyExchange),
  get refundedMoney() {
    throw new Error('Look at the refund on the transaction and match by payment token');
  },
  creatorDetails: attr(CreatorDetails),
  device: attr(Device),
  customer: attr(Customer),
  tenderedMoney: attr(MoneyProto),
  changeBackMoney: attr(MoneyProto),
  tipMoney: attr(MoneyProto),
  otherTenderType: attr(OtherTenderType),
  hashedGiftCardToken: attr(String),
  giftCardToken: attr(String),
  settlementDetails: attr(SettlementDetails),
  externalTender: attr(ExternalTender),
  tenderedAt: attr(DateTime),
  linkedTenderServerToken: attr(String),
  riskEvaluationDetails: attr(RiskEvaluationDetails),
  pushTender: attr(PushTender),
  isAfterpayVirtualCard: attr(Boolean),
  pushTenderType: computed('pushTender', function () {
    return Object.keys(this.pushTender.getAllProperties()).find(k => isPresent(this.pushTender.get(k)));
  }),
  isNoSale: equal('type', Type.NO_SALE),
  isBankTransfer: equal('type', Type.BANK_TRANSFER),
  isCreditCard: equal('type', Type.CREDIT_CARD),
  isCard: equal('type', TenderType.CARD),
  isCash: equal('type', Type.CASH),
  isTab: equal('type', Type.TAB),
  isSquareGiftCard: equal('type', Type.SQUARE_GIFT_CARD),
  isExternalApi: computed('entryMethod', 'isExternal', function () {
    return this.isExternal || EXTERNAL_API_ENTRY_METHODS.includes(this.entryMethod);
  }),
  isOther: equal('type', Type.OTHER),
  isSquareCapital: equal('type', Type.SQUARE_CAPITAL),
  isSquareGiftCardV2: equal('cardBrand', CardBrand.SQUARE_GIFT_CARD_V2),
  isEMoney: equal('type', Type.EMONEY),
  isExternal: equal('type', Type.EXTERNAL),
  isPushTender: equal('type', Type.PUSH_TENDER),
  squareAccountTender: attr(SquareAccountTender),
  isSquareAccountTender: equal('type', Type.SQUARE_ACCOUNT),
  primaryDisplayName: readOnly('squareAccountTender.primaryDisplayName'),
  hasAfterpayTender: notEmpty('pushTender.afterpayTender'),
  hasAfterpayBuyerEmail: notEmpty('pushTender.afterpayTender.readOnlyBuyerEmail'),
  hasCashAppTender: notEmpty('pushTender.cashAppTender'),
  hasCashTag: notEmpty('pushTender.cashAppTender.readOnlyCashtag'),
  hasCashBuyerName: notEmpty('pushTender.cashAppTender.readOnlyBuyerFullName'),
  hasCashTagWithName: and('hasCashTag', 'hasCashBuyerName'),
  isPayWithAfterpay: and('isPushTender', 'hasAfterpayTender'),
  isPayWithCash: and('isPushTender', 'hasCashAppTender'),
  hasCardDetails: or('isCreditCard', 'isSquareGiftCard', 'isEMoney', 'isCard'),
  hasProcessingFee: gt('processingFeeMoney.amount', 0),
  settlementToken: readOnly('settlementDetails.settlementToken'),
  buyerCashTag: readOnly('pushTender.cashAppTender.readOnlyCashtag'),
  buyerCashAppFullName: readOnly('pushTender.cashAppTender.readOnlyBuyerFullName'),
  buyerEmailAfterpay: readOnly('pushTender.afterpayTender.readOnlyBuyerEmail'),
  // This is best effort and will return true for various scenarios where the tender is actually not refundable.
  // Things this does not factor in:
  // 1) If the transaction to which this tender belongs is not refundable (BillListService.getBillRefundableStatus for that)
  // 2) If there are refunds for the total amount of this tender. In that case getResidualBill's residual tender won't have an amount.
  // In general, we should be checking whether or not the transaction is refundable, then relying on getResidualBill, rather
  // than using this property.
  isRefundable: computed('totalMoney.amount', 'disputedPayment', function () {
    return this.get('totalMoney.amount') > 0 && !this.disputedPayment;
  }),
  // these attrs are populated in TransactionsShowRoute (so only visible in that route)
  nameOnCard: attr(String),
  // from rolodex
  contact: attr(Contact),
  disputedPayment: attr(DisputedPayment),
  // from cbms

  // Tells whether a refund for this payment requires the card to be presented
  refundCardPresenceRequirement: attr(String),
  refundRequiresCardPresence: equal('refundCardPresenceRequirement', 'REFUND_CARD_PRESENCE_REQUIRED'),
  hasChange: computed('changeBackMoney.amount', function () {
    return this.get('changeBackMoney.amount') && this.get('changeBackMoney.amount') !== 0;
  }),
  hasTip: computed('tipMoney.amount', function () {
    return this.get('tipMoney.amount') && this.get('tipMoney.amount') !== 0;
  }),
  receiptURL: computed('paymentToken', function () {
    return `/receipt/preview/${this.paymentToken}?dashboard=1`;
  }),
  receiptNumber: computed('paymentToken', function () {
    if (this.paymentToken) {
      return this.paymentToken.slice(0, 4);
    }
    return undefined;
  }),
  riskEvaluationResult: readOnly('riskEvaluationDetails.evaluationResult'),
  isBitcoinPayment: equal('buyerCurrencyExchange.amountMoney.currencyCode', 'BTC'),
  buyerExchangeRate: computed('buyerCurrencyExchange.{amountMoney.currencyCode,exchangeRate}', 'isBitcoinPayment', function () {
    const exchangeRate = this.get('buyerCurrencyExchange.exchangeRate');
    const currencyCode = this.get('buyerCurrencyExchange.amountMoney.currencyCode');
    const amount = Number(exchangeRate ?? 0) * (this.isBitcoinPayment ? 100 : 1); // bitcoin is displayed in satoshis

    const truncatedAmount = Math.floor(amount);
    return {
      amount: truncatedAmount,
      currencyCode
    };
  }),
  sellerExchangeRate: computed('totalMoney.{currency,currencyCode}', function () {
    const currencyCode = this.get('totalMoney.currencyCode');
    return Money.create({
      amount: 100,
      currencyCode
    });
  })
}).reopenClass({
  // primaryKey: 'paymentToken' # FIXME (carden): this should be helpful but it causes scary errors
});